// Client bootstrap. Integrator-owned.
// Routes ?showcase=<name> to a module's showcase.js, otherwise boots the full game (./game.js).
// Exposes window.__joust for the screenshot tool and the harness:
//   __joust.ready            true once the active mode has finished loading
//   __joust.errors           array of recorded errors (see shared/isolate.js)
//   __joust.degraded         modules disabled by a failure
//   __joust.api              { setCamera(preset), setTimeOfDay(hours), stats() -> {fps, drawCalls, triangles, textures} }
//   __joust.mode             'game' | 'showcase:<name>'
import { getErrors, getDegraded, recordError } from '../shared/isolate.js';
import { createSplash } from './loading.js';

// import.meta.glob tolerates missing files: a module whose showcase does not exist yet simply is not listed.
const SHOWCASE_FILES = import.meta.glob(['../*/showcase.js', './smoke.js']);
const SHOWCASE_NAMES = {
  arena: '../arena-environment/showcase.js',
  render: '../client-render/showcase.js',
  input: '../client-input/showcase.js',
  audio: '../client-audio/showcase.js',
  ui: '../ui/showcase.js',
  replay: '../demo-replay/showcase.js',
  net: '../net/showcase.js',
  hit: '../lance-hit-detection/showcase.js',
  horse: '../horse-physics/showcase.js',
  scoring: '../scoring/showcase.js',
  smoke: './smoke.js',
};
const SHOWCASES = Object.fromEntries(
  Object.entries(SHOWCASE_NAMES).filter(([, file]) => SHOWCASE_FILES[file]).map(([name, file]) => [name, SHOWCASE_FILES[file]]),
);

const params = new URLSearchParams(location.search);
const canvas = document.getElementById('game');
const uiRoot = document.getElementById('ui');

window.__joust = {
  ready: false,
  mode: 'boot',
  api: {},
  get errors() {
    return getErrors();
  },
  get degraded() {
    return getDegraded();
  },
};

window.addEventListener('error', (e) => recordError('window', e.error || e.message, 'uncaught'));
window.addEventListener('unhandledrejection', (e) => recordError('window', e.reason, 'unhandledrejection'));

// The boot splash lives in index.html so it paints before this module is even fetched. Every mode
// gets it taken away again here — a showcase must not have to know it exists, and a splash left up
// over a showcase is an opaque page and a blank screenshot.
const splash = createSplash(document);
window.__joust_splash = splash;

const ctx = { canvas, uiRoot, params, expose, splash };

/** Showcases and the game call expose({ setCamera, setTimeOfDay, stats }) then setReady(). */
function expose(api) {
  Object.assign(window.__joust.api, api);
}
export function setReady() {
  window.__joust.ready = true;
}
export function fatal(message) {
  const el = document.getElementById('fatal');
  el.textContent = String(message);
  el.style.display = 'grid';
  splash.done();
  recordError('boot', message, 'fatal');
}

async function boot() {
  const showcase = params.get('showcase');
  try {
    if (showcase) {
      const loader = SHOWCASES[showcase];
      if (!loader) throw new Error(`Unknown showcase "${showcase}". Known: ${Object.keys(SHOWCASES).join(', ')}`);
      window.__joust.mode = `showcase:${showcase}`;
      splash.note('Loading showcase');
      const mod = await loader();
      await mod.default(ctx);
    } else {
      window.__joust.mode = 'game';
      const mod = await import('./game.js');
      await mod.default(ctx);
    }
    setReady();
  } catch (err) {
    fatal(`Failed to start (${window.__joust.mode}): ${err && err.message}`);
    console.error(err);
    window.__joust.ready = true; // let tools read the error instead of hanging
  } finally {
    // Whatever happened — a showcase that never heard of the splash, a mode that threw — the page
    // is now this module's problem no longer, so the cover comes off. game.js normally calls this
    // itself the moment the title is up; `done()` is idempotent.
    splash.done();
  }
}

boot();
